import { useMutation } from "@tanstack/react-query";

/**
 * Public (anonymous) client for the AI onboarding flow. Step 2 posts a website
 * URL to /public/intake/profile-draft and gets back a draft profile the
 * contractor reviews. These routes are NOT JWT-authorized (the pro has no
 * account yet), so this fetch never attaches a token — distinct from
 * portal/api.ts.
 */

export interface DraftTestimonial {
  quote: string;
  author: string | null;
}

export interface DraftSocialLinks {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
  twitter?: string;
}

/** Mirror of the backend ProfileDraft (contractor/api/src/domain/profile-draft.ts). */
export interface ProfileDraft {
  source: "website" | "manual";
  website: string;
  logoUrl: string | null;
  businessName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  serviceAreaText: string | null;
  serviceAreaZips: string[];
  serviceAreaCities: string[];
  serviceCategories: string[];
  capabilities: string[];
  servicesOffered: string[];
  emergencyService: boolean | null;
  yearsInBusiness: number | null;
  description: string | null;
  certifications: string[];
  licenses: string[];
  insuranceMentioned: boolean | null;
  awards: string[];
  testimonials: DraftTestimonial[];
  socialLinks: DraftSocialLinks;
  photos: string[];
  confidence: number;
}

type ProfileDraftResponse = { draft: ProfileDraft } | { fallback: "manual" };

/** A blank draft for the "I don't have a website" path and the manual fallback. */
export function emptyDraft(source: "manual" = "manual"): ProfileDraft {
  return {
    source,
    website: "",
    logoUrl: null,
    businessName: null,
    phone: null,
    email: null,
    address: null,
    serviceAreaText: null,
    serviceAreaZips: [],
    serviceAreaCities: [],
    serviceCategories: [],
    capabilities: [],
    servicesOffered: [],
    emergencyService: null,
    yearsInBusiness: null,
    description: null,
    certifications: [],
    licenses: [],
    insuranceMentioned: null,
    awards: [],
    testimonials: [],
    socialLinks: {},
    photos: [],
    confidence: 0,
  };
}

export async function publicFetch<T = unknown>(path: string, json?: unknown): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error("VITE_API_BASE_URL is not set in this build.");
  }
  const url = baseUrl.replace(/\/$/, "") + (path.startsWith("/") ? path : `/${path}`);
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(json ?? {}),
  });
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new Error(`API ${res.status}`);
  }
  return parsed as T;
}

/**
 * Generate a draft from a website URL. Always resolves to a ProfileDraft:
 * the server returns `{ fallback: "manual" }` when it can't crawl/extract, and
 * we turn that into a blank draft so the wizard can always proceed to review.
 */
export function useGenerateDraft() {
  return useMutation<ProfileDraft, Error, string>({
    mutationFn: async (websiteUrl: string) => {
      const res = await publicFetch<ProfileDraftResponse>("/public/intake/profile-draft", {
        websiteUrl,
      });
      if ("draft" in res) return res.draft;
      return { ...emptyDraft("manual"), website: websiteUrl.trim() };
    },
  });
}
